<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters } from 'dashboard/composables/store';
import { frontendURL } from 'dashboard/helper/URLHelper';
import TalkviaCalendarIntegrationAPI from 'dashboard/api/talkviaCalendarIntegration';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import GoogleCalendarAvailability from './GoogleCalendarAvailability.vue';
import GoogleLimitedUseNotice from 'dashboard/components-next/google/GoogleLimitedUseNotice.vue';

const route = useRoute();
const getters = useStoreGetters();
const accountId = getters.getCurrentAccountId;

const isLoading = ref(false);
const isSaving = ref(false);
const isConnecting = ref(false);
const isDeleting = ref(false);
const deleteDialogRef = ref(null);
const deleteError = ref('');
const showAdvanced = ref(false);
const integration = ref(null);

const googleLogo = '/brand-assets/integrations/google.png';

const copy = {
  loading: 'Carregando Google Agenda...',
  title: 'Google Agenda',
  description:
    'Sincronize os compromissos da Talkvia com o Google Agenda da empresa e gere links do Google Meet.',
  statusActive: 'Conectado e ativo',
  statusInactive: 'Conectado, sincronização desligada',
  statusDisconnected: 'Não conectado',
  connectedHint: 'A conta Google autorizou o acesso à agenda da empresa.',
  connectedEmail: 'Conta conectada:',
  emailUnavailable:
    'E-mail da conta indisponível. Reconecte o Google para atualizar a identificação.',
  disconnectedHint:
    'Conecte uma conta Google com acesso à agenda que a equipe usa para os atendimentos.',
  reconnect: 'Reconectar Google',
  connect: 'Conectar Google',
  reconnectTitle: 'Reconecte o Google para liberar a Agenda da IA',
  reconnectHint:
    'A conexão atual não autoriza a consulta de horários ocupados. Clique em Reconectar Google e confirme todas as permissões pedidas.',
  active: 'Sincronizar com o Google Agenda',
  activeHint:
    'Desligado, a Talkvia não lê nem grava eventos no Google, e a IA não oferece horários.',
  calendarId: 'ID do calendário',
  calendarIdPlaceholder: 'Em branco usa a agenda principal da conta',
  calendarIdHint:
    'Para outra agenda, cole o ID ou o link de incorporação (Configurações da agenda no Google → Integrar agenda).',
  syncDirection: 'Direção da sincronização',
  advanced: 'Avançado',
  webhookUrl: 'Webhook / sistema externo',
  webhookUrlPlaceholder: 'https://...',
  webhookHint:
    'Opcional: endereço que recebe os eventos da agenda em outro sistema.',
  save: 'Salvar integração',
  loadFailed: 'Não foi possível carregar a integração do Google Agenda.',
  saveSuccess: 'Integração salva.',
  saveFailed: 'Não foi possível salvar a integração.',
  authorizationFailed: 'Não foi possível iniciar a conexão com o Google.',
  googleConnected: 'Google Agenda conectado.',
  googleConnectionFailed: 'Não foi possível conectar o Google Agenda.',
  deleteConnection: 'Excluir conexão',
  deleteTitle: 'Excluir conexão com o Google Agenda?',
  deleteDescription:
    'O vínculo com a conta Google será removido e a sincronização será desligada. Os compromissos existentes na Talkvia e no Google Agenda serão preservados. Você poderá conectar uma conta novamente quando quiser.',
  deleteSuccess: 'Conexão excluída. Os compromissos foram preservados.',
  deleteFailed: 'Não foi possível excluir a conexão. Tente novamente.',
  cancel: 'Cancelar',
};

const form = reactive({
  active: false,
  calendar_id: '',
  webhook_url: '',
  sync_direction: 'two_way',
});

const syncDirectionOptions = [
  {
    value: 'two_way',
    label: 'Duas vias',
    badge: 'Recomendado',
    icon: 'i-hugeicons-arrow-data-transfer-horizontal',
    description: 'Eventos criados em qualquer um dos lados aparecem nos dois.',
  },
  {
    value: 'talkvia_to_external',
    label: 'Talkvia para o Google',
    icon: 'i-hugeicons-arrow-right-02',
    description:
      'A Talkvia grava no Google; eventos criados no Google não voltam.',
  },
  {
    value: 'external_to_talkvia',
    label: 'Google para a Talkvia',
    icon: 'i-hugeicons-arrow-left-02',
    description:
      'Só traz eventos do Google. A IA não consegue marcar horários.',
  },
];

const hasGoogleCredentials = computed(() =>
  Boolean(integration.value?.has_credentials)
);

const isCalendarReady = computed(
  () => hasGoogleCredentials.value && Boolean(integration.value?.active)
);

// false: conexão sem a permissão de horários ocupados; null: conexão antiga,
// anterior ao registro das permissões.
const needsReconnect = computed(
  () =>
    hasGoogleCredentials.value && integration.value?.freebusy_granted !== true
);

const status = computed(() => {
  if (!hasGoogleCredentials.value)
    return {
      label: copy.statusDisconnected,
      class: 'bg-n-alpha-2 text-n-slate-11',
      dot: 'bg-n-slate-9',
    };
  if (integration.value?.active)
    return {
      label: copy.statusActive,
      class: 'bg-n-teal-3 text-n-teal-11',
      dot: 'bg-n-teal-9',
    };
  return {
    label: copy.statusInactive,
    class: 'bg-n-amber-3 text-n-amber-11',
    dot: 'bg-n-amber-9',
  };
});

const returnTo = computed(() =>
  frontendURL(
    `accounts/${accountId.value}/settings/integrations/google_calendar`
  )
);

const isBusy = computed(
  () =>
    isLoading.value || isSaving.value || isConnecting.value || isDeleting.value
);

const applyIntegration = data => {
  integration.value = data;
  Object.assign(form, {
    active: Boolean(data?.active),
    calendar_id: data?.calendar_id || '',
    webhook_url: data?.webhook_url || '',
    sync_direction: data?.sync_direction || 'two_way',
  });
  showAdvanced.value = Boolean(data?.webhook_url);
};

const requestDeleteConnection = () => {
  if (isBusy.value || !hasGoogleCredentials.value) return;
  deleteError.value = '';
  deleteDialogRef.value?.open();
};

const deleteConnection = async () => {
  if (isBusy.value || !hasGoogleCredentials.value) return;
  isDeleting.value = true;
  deleteError.value = '';
  try {
    const { data } = await TalkviaCalendarIntegrationAPI.delete();
    applyIntegration(data);
    deleteDialogRef.value?.close();
    useAlert(copy.deleteSuccess);
  } catch {
    deleteError.value = copy.deleteFailed;
  } finally {
    isDeleting.value = false;
  }
};

const updateAvailability = data => {
  if (isDeleting.value || !integration.value) return;
  integration.value = {
    ...integration.value,
    availability: data.availability,
    upcoming_holidays: data.upcoming_holidays || [],
  };
};

const loadIntegration = async () => {
  isLoading.value = true;
  try {
    const { data } = await TalkviaCalendarIntegrationAPI.get();
    applyIntegration(data);
  } catch {
    useAlert(copy.loadFailed);
  } finally {
    isLoading.value = false;
  }
};

const saveIntegration = async () => {
  if (isBusy.value) return;
  isSaving.value = true;
  try {
    const { data } = await TalkviaCalendarIntegrationAPI.update(form);
    integration.value = data;
    useAlert(copy.saveSuccess);
  } catch {
    useAlert(copy.saveFailed, { variant: 'error' });
  } finally {
    isSaving.value = false;
  }
};

const connectGoogleCalendar = async () => {
  if (isBusy.value) return;
  isConnecting.value = true;
  try {
    const {
      data: { url },
    } = await TalkviaCalendarIntegrationAPI.generateAuthorization({
      return_to: returnTo.value,
    });
    window.location.href = url;
  } catch {
    useAlert(copy.authorizationFailed, { variant: 'error' });
  } finally {
    isConnecting.value = false;
  }
};

onMounted(() => {
  if (route.query.google_calendar === 'connected') {
    useAlert(copy.googleConnected);
  } else if (route.query.google_calendar === 'failed') {
    useAlert(copy.googleConnectionFailed, { variant: 'error' });
  }

  loadIntegration();
});
</script>

<template>
  <SettingsLayout :is-loading="isLoading" :loading-message="copy.loading">
    <template #header>
      <BaseSettingsHeader :title="copy.title" :description="copy.description" />
    </template>

    <template #body>
      <form
        class="grid max-w-3xl gap-6 rounded-xl border border-n-weak bg-n-card p-6"
        data-testid="google-calendar-form"
        @submit.prevent="saveIntegration"
      >
        <header
          class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between"
        >
          <div class="flex min-w-0 items-start gap-4">
            <span
              class="grid size-12 shrink-0 place-items-center rounded-xl bg-white ring-1 ring-n-weak"
            >
              <img
                :src="googleLogo"
                alt="Google"
                class="size-7 object-contain"
              />
            </span>
            <div class="min-w-0">
              <div class="mb-1 flex flex-wrap items-center gap-2">
                <h3 class="mb-0 text-lg font-semibold text-n-slate-12">
                  {{ copy.title }}
                </h3>
                <span
                  class="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium"
                  :class="status.class"
                  data-testid="google-calendar-status"
                >
                  <span class="size-1.5 rounded-full" :class="status.dot" />
                  {{ status.label }}
                </span>
              </div>
              <p class="mb-0 text-sm text-n-slate-11">
                {{
                  hasGoogleCredentials
                    ? copy.connectedHint
                    : copy.disconnectedHint
                }}
              </p>
              <p
                v-if="hasGoogleCredentials"
                class="mb-0 mt-2 break-all text-sm text-n-slate-12"
                data-testid="google-connected-email"
              >
                <template v-if="integration.connected_email">
                  <span class="text-n-slate-11">{{ copy.connectedEmail }}</span>
                  {{ ' '
                  }}<span class="font-medium">{{
                    integration.connected_email
                  }}</span>
                </template>
                <span v-else class="text-xs text-n-slate-11">{{
                  copy.emailUnavailable
                }}</span>
              </p>
            </div>
          </div>
          <div class="flex shrink-0 flex-wrap items-center gap-2">
            <Button
              type="button"
              :color="
                hasGoogleCredentials && !needsReconnect ? 'slate' : 'blue'
              "
              :variant="
                hasGoogleCredentials && !needsReconnect ? 'faded' : 'solid'
              "
              icon="i-hugeicons-link-04"
              :label="hasGoogleCredentials ? copy.reconnect : copy.connect"
              :is-loading="isConnecting"
              :disabled="isBusy"
              class="shrink-0"
              data-testid="google-connect"
              @click="connectGoogleCalendar"
            />
            <Button
              v-if="hasGoogleCredentials"
              type="button"
              color="ruby"
              variant="faded"
              icon="i-hugeicons-delete-02"
              :label="copy.deleteConnection"
              :is-loading="isDeleting"
              :disabled="isBusy"
              data-testid="google-delete-connection"
              @click="requestDeleteConnection"
            />
          </div>
        </header>

        <GoogleLimitedUseNotice />

        <div
          v-if="needsReconnect"
          class="flex items-start gap-3 rounded-lg border border-n-amber-6 bg-n-amber-2 p-3"
          data-testid="google-reconnect-warning"
        >
          <span
            class="i-hugeicons-alert-02 mt-0.5 size-4 shrink-0 text-n-amber-11"
            aria-hidden="true"
          />
          <div class="min-w-0">
            <p class="mb-0 text-sm font-medium text-n-amber-12">
              {{ copy.reconnectTitle }}
            </p>
            <p class="mb-0 text-xs text-n-amber-11">
              {{ copy.reconnectHint }}
            </p>
          </div>
        </div>

        <section
          class="flex items-start justify-between gap-4 rounded-lg border border-n-weak bg-n-solid-1 p-4"
        >
          <div class="min-w-0">
            <p class="mb-0 text-sm font-medium text-n-slate-12">
              {{ copy.active }}
            </p>
            <p class="mb-0 text-xs text-n-slate-11">{{ copy.activeHint }}</p>
          </div>
          <Switch v-model="form.active" data-testid="google-active" />
        </section>

        <section class="grid gap-1.5">
          <Input
            v-model="form.calendar_id"
            :label="copy.calendarId"
            :placeholder="copy.calendarIdPlaceholder"
          />
          <p class="mb-0 text-xs text-n-slate-11">{{ copy.calendarIdHint }}</p>
        </section>

        <section class="grid gap-2">
          <span class="text-heading-3 text-n-slate-12">
            {{ copy.syncDirection }}
          </span>
          <div class="grid gap-2 sm:grid-cols-3" role="radiogroup">
            <button
              v-for="option in syncDirectionOptions"
              :key="option.value"
              type="button"
              role="radio"
              :aria-checked="form.sync_direction === option.value"
              class="grid content-start gap-1.5 rounded-xl border border-solid p-3 text-left transition-colors"
              :class="
                form.sync_direction === option.value
                  ? 'border-n-brand bg-n-brand/10'
                  : 'border-n-weak hover:bg-n-alpha-2'
              "
              :data-testid="`sync-${option.value}`"
              @click="form.sync_direction = option.value"
            >
              <span class="flex items-center gap-2">
                <span
                  class="size-4 shrink-0"
                  :class="[
                    option.icon,
                    form.sync_direction === option.value
                      ? 'text-n-brand'
                      : 'text-n-slate-11',
                  ]"
                  aria-hidden="true"
                />
                <span class="text-sm font-medium text-n-slate-12">
                  {{ option.label }}
                </span>
              </span>
              <span
                v-if="option.badge"
                class="w-fit rounded-full bg-n-teal-3 px-2 py-0.5 text-[11px] font-medium text-n-teal-11"
              >
                {{ option.badge }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{ option.description }}
              </span>
            </button>
          </div>
        </section>

        <section class="grid gap-3 border-t border-n-weak pt-4">
          <button
            type="button"
            class="flex w-fit items-center gap-1.5 text-sm font-medium text-n-slate-11 hover:text-n-slate-12"
            :aria-expanded="showAdvanced"
            data-testid="google-advanced"
            @click="showAdvanced = !showAdvanced"
          >
            <span
              class="i-hugeicons-arrow-down-01 size-4 transition-transform"
              :class="{ '-rotate-90': !showAdvanced }"
              aria-hidden="true"
            />
            {{ copy.advanced }}
          </button>
          <div v-if="showAdvanced" class="grid gap-1.5">
            <Input
              v-model="form.webhook_url"
              type="url"
              :label="copy.webhookUrl"
              :placeholder="copy.webhookUrlPlaceholder"
            />
            <p class="mb-0 text-xs text-n-slate-11">{{ copy.webhookHint }}</p>
          </div>
        </section>

        <div class="flex justify-end">
          <Button
            type="submit"
            :label="copy.save"
            :is-loading="isSaving"
            :disabled="isBusy"
            data-testid="google-save"
          />
        </div>
      </form>

      <GoogleCalendarAvailability
        v-if="integration"
        :availability="integration.availability"
        :upcoming-holidays="integration.upcoming_holidays || []"
        :connected="isCalendarReady"
        @saved="updateAvailability"
      />
      <Dialog
        ref="deleteDialogRef"
        type="alert"
        :title="copy.deleteTitle"
        :description="copy.deleteDescription"
        :confirm-button-label="copy.deleteConnection"
        :cancel-button-label="copy.cancel"
        :is-loading="isDeleting"
        :show-cancel-button="!isDeleting"
        :close-on-click-outside="!isDeleting"
        @confirm="deleteConnection"
      >
        <p v-if="deleteError" role="alert" class="m-0 text-sm text-n-ruby-11">
          {{ deleteError }}
        </p>
      </Dialog>
    </template>
  </SettingsLayout>
</template>
